/**
 * Discount code (voucher / referral) section for InvoiceManager.
 * Validates codes via code-validate, auto-applies discount amount.
 */

import styles from './invoiceManagerStyles';

interface CodeValidationResult {
  valid: boolean;
  message: string;
  code_type?: 'referral' | 'voucher';
  discount_amount?: number;
  voucher_id?: string;
  referral_code_id?: string;
  payout_type?: 'discount' | 'commission';
  // True when a commission code is also a live collaboration, which discounts
  // the customer as well as paying the partner. Plain agent codes are false.
  collab_discount?: boolean;
  [key: string]: unknown;
}

export interface DiscountCodeSectionProps {
  discountCode: string;
  onSetDiscountCode: (v: string) => void;
  codeValidating: boolean;
  codeValidation: CodeValidationResult | null;
  onValidateCode: () => void;
  onClearCode: () => void;
  customerEmail: string | null | undefined;
  formatCurrency: (amount: number) => string;
  alreadyAppliedDiscount?: number;
  /** The part of it that came from a booking code, which an edit cannot change. */
  codeDiscount?: number;
}

export default function DiscountCodeSection({
  discountCode, onSetDiscountCode,
  codeValidating, codeValidation,
  onValidateCode, onClearCode,
  customerEmail, formatCurrency,
  alreadyAppliedDiscount = 0,
  codeDiscount = 0,
}: DiscountCodeSectionProps) {
  // A discount is already on this invoice (carried through from a booking code,
  // an applied code, or an operator discount). Applying a code on top would
  // stack and trip the invoice total check, so show the existing one instead.
  if (alreadyAppliedDiscount > 0) {
    return (
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Discount Code</h2>
        <p className="text-sm" style={{ color: 'var(--color-text-light)' }}>
          A discount of {formatCurrency(alreadyAppliedDiscount)} is already applied to this invoice. Adjust it in the Discount section below.
        </p>
        {codeDiscount > 0 && (
          <p className="text-sm mt-sm" style={{ color: 'var(--color-text-light)' }}>
            {formatCurrency(codeDiscount)} of that came from a code used at booking and stays on the invoice. Anything you enter below comes off on top of it.
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h2 style={styles.cardTitle}>Discount Code</h2>
      <p className="text-sm mb-md" style={{ color: 'var(--color-text-light)' }}>
        Apply a voucher or referral code to this invoice. The discount will be added as a negative line item.
      </p>
      <div className="flex gap-md items-center">
        <input
          type="text"
          value={discountCode}
          onChange={(e) => onSetDiscountCode(e.target.value.toUpperCase())}
          placeholder="e.g., R-SMITH-ABC or V-PARTNER-1234"
          style={{ ...styles.input, fontFamily: 'monospace', textTransform: 'uppercase', flex: 1 }}
          maxLength={25}
          onKeyDown={(e) => e.key === 'Enter' && onValidateCode()}
        />
        <button
          onClick={onValidateCode}
          disabled={codeValidating || !discountCode.trim() || !customerEmail}
          style={{
            ...styles.primaryBtn,
            opacity: (codeValidating || !discountCode.trim()) ? 0.5 : 1,
          }}
        >
          {codeValidating ? 'Checking...' : 'Validate'}
        </button>
        {codeValidation?.valid && (
          <button
            onClick={onClearCode}
            style={{ ...styles.grayBtn, padding: '0.75rem 1rem' }}
            title="Remove discount code"
          >
            Clear
          </button>
        )}
      </div>

      {!customerEmail && (
        <p className="text-sm mt-sm" style={{ color: 'var(--color-warning)' }}>
          Customer email is required to validate a discount code
        </p>
      )}

      {codeValidation && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.75rem 1rem',
          borderRadius: 'var(--radius)',
          background: codeValidation.valid ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
          border: `1px solid ${codeValidation.valid ? '#bbf7d0' : '#fecaca'}`,
          color: codeValidation.valid ? 'var(--color-success-text)' : 'var(--color-error)',
          fontSize: 'var(--text-base)',
        }}>
          {codeValidation.valid ? '\u2713' : '\u2717'} {codeValidation.message}
          {codeValidation.valid && codeValidation.discount_amount && codeValidation.discount_amount > 0 && !(codeValidation.payout_type === 'commission' && !codeValidation.collab_discount) && (
            <span className="fw-600">
              {' '} &mdash; {formatCurrency(codeValidation.discount_amount)} discount auto-applied below
            </span>
          )}
          {codeValidation.valid && codeValidation.payout_type === 'commission' && !codeValidation.collab_discount && (
            <span className="fw-600">
              {' '} &mdash; Commission will be deducted before revenue split
            </span>
          )}
        </div>
      )}
    </div>
  );
}
